import Seo from "../components/Seo";
import { ArrowRight, ClipboardList, ListChecks, MailCheck, UserCheck } from "lucide-react";
import { ENROL_URL, IMAGES } from "../lib/site";
import { FadeIn } from "../components/FadeIn";
import PageHeader from "../components/PageHeader";

const steps = [
  {
    icon: ClipboardList,
    title: "Join the waiting list",
    desc: "Complete our online waiting list form - it takes a few minutes and places your child in line for the sessions you need.",
  },
  {
    icon: MailCheck,
    title: "Receive an offer",
    desc: "When a place becomes available, we contact you with an offer for your requested sessions (permanent or casual).",
  },
  {
    icon: ListChecks,
    title: "Complete enrolment",
    desc: "Accept the offer and complete your child's enrolment details, including medical information and authorised contacts.",
  },
  {
    icon: UserCheck,
    title: "Set up CCS & start",
    desc: "Confirm the enrolment in myGov to activate your Child Care Subsidy, then your child is ready for their first session - we'll make it a warm one.",
  },
];

const Enrolment = () => (
  <div data-testid="enrolment-page">
    <Seo
      title="Enrol & Waitlist | Telopea Park OSHC"
      description="Join the waitlist and enrol your child at Telopea Park OSHC, Barton, Canberra - not-for-profit, community-run Out of School Hours Care."
      path="/enrol"
    />
    <PageHeader
      testId="enrolment-header"
      eyebrow="Enrolment"
      title="Join the Telopea Park OSHC family"
      description="Places at our service are offered through our online waiting list. Here's how it works - the first step takes just a few minutes."
    />

    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-16 sm:py-20" data-testid="enrolment-steps">
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((s, i) => (
          <FadeIn key={s.title} delay={i * 0.08}>
            <div className="relative h-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid={`enrolment-step-${i + 1}`}>
              <span className="absolute top-6 right-7 font-display text-4xl font-bold text-navy-100">{i + 1}</span>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-waratah-50 text-waratah">
                <s.icon className="h-6 w-6" />
              </div>
              <h2 className="mt-6 font-display text-2xl font-semibold text-navy">{s.title}</h2>
              <p className="mt-3 text-base text-slate-600 leading-relaxed">{s.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>

      <FadeIn delay={0.15}>
        <div className="mt-12 rounded-3xl bg-navy p-10 sm:p-14 text-center" data-testid="enrolment-cta">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white">Ready to take the first step?</h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-white/80 leading-relaxed">
            Join our waiting list online. We&rsquo;ll be in touch as soon as a place is available for your family.
          </p>
          <a
            href={ENROL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="enrolment-enrol-button"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-waratah px-10 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-waratah-600 hover:shadow-xl"
          >
            Enrol Now <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </FadeIn>
    </section>

    <section className="bg-surface border-t border-navy-100" data-testid="enrolment-notes">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <FadeIn>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy">Good to know</h2>
            <ul className="mt-8 space-y-5 text-lg text-slate-600 leading-relaxed">
              <li data-testid="enrolment-note-1">
                <strong className="text-navy">Permanent and casual bookings.</strong> Permanent bookings reserve the
                same sessions each week; casual bookings can be made subject to availability.
              </li>
              <li data-testid="enrolment-note-2">
                <strong className="text-navy">Child Care Subsidy.</strong> Most families receive CCS, which is paid
                directly to the service and reduces your fees. See our Fees & CCS page for an estimate.
              </li>
              <li data-testid="enrolment-note-3">
                <strong className="text-navy">Priority of access.</strong> Places are allocated in line with the
                Australian Government&rsquo;s Priority of Access guidelines and our Enrolment &amp; Orientation policy.
              </li>
              <li data-testid="enrolment-note-4">
                <strong className="text-navy">Orientation.</strong> Every new child is offered an orientation visit so
                their first session already feels familiar.
              </li>
            </ul>
          </FadeIn>
          <FadeIn delay={0.1}>
            <img
              src={IMAGES.craftsHands}
              alt="Children's hands working on a craft activity"
              className="w-full rounded-3xl object-cover shadow-lg aspect-[4/3]"
              data-testid="enrolment-image"
            />
          </FadeIn>
        </div>
      </div>
    </section>
  </div>
);

export default Enrolment;
